import { getAllTechReportForms, saveTechReportForm, submitTechReportForm, getTechReportForm, updateFormStatus, deleteTechReportForm } from '../../apis/tech-report-forms';
import { getSchedulesByUserAndOrder } from '../../apis/schedule';

export const techReportActionTypes = {
  SET_WORK_ORDER_CONTEXT: 'TECH_REPORT/SET_WORK_ORDER_CONTEXT',
  FETCH_FORMS_START: 'TECH_REPORT/FETCH_FORMS_START',
  FETCH_FORMS_SUCCESS: 'TECH_REPORT/FETCH_FORMS_SUCCESS',
  FETCH_FORMS_FAILURE: 'TECH_REPORT/FETCH_FORMS_FAILURE',
  SET_ACTIVE_TAB_KEY: 'TECH_REPORT/SET_ACTIVE_TAB_KEY',
  SET_FORM_SAVING: 'TECH_REPORT/SET_FORM_SAVING',
  CLEAR_WORK_ORDER_CONTEXT: 'TECH_REPORT/CLEAR_WORK_ORDER_CONTEXT',
};

/** Action Creators */
export const setWorkOrderContext = (contextData) => ({
  type: techReportActionTypes.SET_WORK_ORDER_CONTEXT,
  payload: contextData,
});

export const clearWorkOrderContext = () => ({
  type: techReportActionTypes.CLEAR_WORK_ORDER_CONTEXT,
});

export const setActiveTabKey = (tabKey) => ({
  type: techReportActionTypes.SET_ACTIVE_TAB_KEY,
  payload: tabKey,
});

export const fetchFormsStart = () => ({
  type: techReportActionTypes.FETCH_FORMS_START,
});

export const fetchFormsSuccess = (forms) => ({
  type: techReportActionTypes.FETCH_FORMS_SUCCESS,
  payload: forms,
});

export const fetchFormsFailure = (error) => ({
  type: techReportActionTypes.FETCH_FORMS_FAILURE,
  payload: error,
});

export const setFormSaving = (isSaving) => ({
  type: techReportActionTypes.SET_FORM_SAVING,
  payload: isSaving,
});

/**
 * Thunk to load tech report forms for a specific user & order
 */
export const loadTechReportForms = (userId, orderId) => async (dispatch) => {
  if (!userId) return;
  try {
    dispatch(fetchFormsStart());
    const params = { 
      user_id: userId,
      exclude_form_type: 'coordinator_assessment',
      exclude_form_name: 'Clinical Coordinator Intake Assessment'
    };
    if (orderId && orderId !== 'new' && orderId !== 'default') {
      params.order_id = orderId;
    }
    const result = await getAllTechReportForms(params);
    if (result && result.success) {
      dispatch(fetchFormsSuccess(result.data || []));
    } else {
      dispatch(fetchFormsFailure(result?.message || 'Failed to load forms'));
    }
  } catch (error) {
    console.error('Error in loadTechReportForms thunk:', error);
    dispatch(fetchFormsFailure(error.message || 'Failed to load forms'));
  }
};

/**
 * Thunk to fetch schedule and update technician/date in work order context
 */
export const fetchOrderScheduleContext = (userId, orderId) => async (dispatch, getState) => {
  if (!userId || !orderId || orderId === 'new' || orderId === 'default') return;
  try {
    const resp = await getSchedulesByUserAndOrder(userId, orderId);
    if (resp && resp.success && resp.data && Array.isArray(resp.data.schedules) && resp.data.schedules.length > 0) {
      const match = resp.data.schedules.find(
        s => String(s.order_id) === String(orderId) && String(s.patient_id) === String(userId)
      ) || resp.data.schedules[0];

      if (match) {
        const currentContext = getState().TechReport?.activeContext || {};
        dispatch(setWorkOrderContext({
          ...currentContext,
          technicianName: match.technician_name || currentContext.technicianName || '',
          scheduledDate: match.date || currentContext.scheduledDate || null,
          startTime: match.start_time || currentContext.startTime || null,
          endTime: match.end_time || currentContext.endTime || null,
          locationName: match.locations_name || currentContext.locationName || '',
          roomName: match.room_name || currentContext.roomName || '',
        }));
      }
    }
  } catch (error) {
    console.error('Error in fetchOrderScheduleContext thunk:', error);
  }
};

/**
 * Thunk to save a tech report form
 */
export const saveTechReportFormThunk = (payload, isDraft = true) => async (dispatch) => {
  try {
    dispatch(setFormSaving(true));
    const result = await saveTechReportForm(payload);
    if (result && result.success) {
      if (payload.user_id) {
        dispatch(loadTechReportForms(payload.user_id, payload.order_id));
      }
      return { success: true, data: result.data };
    } else {
      return { success: false, message: result?.message || 'Failed to save form' };
    }
  } catch (error) {
    console.error('Error in saveTechReportFormThunk:', error);
    return { success: false, message: error.message || 'An error occurred while saving the form' };
  } finally {
    dispatch(setFormSaving(false));
  }
};

/**
 * Thunk to submit a tech report form
 */
export const submitTechReportFormThunk = (payload) => async (dispatch) => {
  try {
    dispatch(setFormSaving(true));
    const result = await submitTechReportForm(payload);
    if (result && result.success) {
      if (payload.user_id) {
        dispatch(loadTechReportForms(payload.user_id, payload.order_id));
      }
      return { success: true, data: result.data };
    } else {
      return { success: false, message: result?.message || 'Failed to submit form' };
    }
  } catch (error) {
    console.error('Error in submitTechReportFormThunk:', error);
    return { success: false, message: error.message || 'An error occurred while submitting the form' };
  } finally {
    dispatch(setFormSaving(false));
  }
};

const techReportActions = {
  ...techReportActionTypes,
  setWorkOrderContext,
  clearWorkOrderContext,
  setActiveTabKey,
  fetchFormsStart,
  fetchFormsSuccess,
  fetchFormsFailure,
  setFormSaving,
  loadTechReportForms,
  fetchOrderScheduleContext,
  saveTechReportFormThunk,
  submitTechReportFormThunk,
};

export default techReportActions;
